import { DATE_UNITS, type WhereDataset, type WhereField, type WhereOption } from '@parcel/filters';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Input,
  cn,
} from '@parcel/ui';
import { Check, X } from 'lucide-react';
import * as React from 'react';
import { fieldTiers } from './tier-shortcuts.js';
import { type WhereOptions, fieldValues } from './where-menu.js';

export interface RuleValueEditorProps {
  dataset: WhereDataset;
  field: WhereField;
  operator: string;
  value: unknown;
  options: WhereOptions;
  onChange: (value: unknown) => void;
  /** Enter in a text or number input. */
  onSubmit?: () => void;
}

const UNIT_LABEL: Record<(typeof DATE_UNITS)[number], string> = { days: 'days', months: 'months' };

/** The value control for one rule, by the field's kind and the operator; no control for empty and not empty. */
export function RuleValueEditor(props: RuleValueEditorProps) {
  const { field, operator } = props;
  if (operator === 'null' || operator === 'notNull') return null;
  switch (field.kind) {
    case 'choice':
      return <OptionList {...props} />;
    case 'text':
      return operator === 'in' || operator === 'notIn' ? (
        <TextList {...props} />
      ) : (
        <TextInput {...props} />
      );
    case 'number':
      return operator === 'between' ? <NumberPair {...props} /> : <NumberInput {...props} />;
    case 'score':
      return operator === 'between' ? <ScoreRange {...props} /> : <NumberInput {...props} />;
    case 'date':
      return operator === 'between' ? <DatePair {...props} /> : <AgoInput {...props} />;
    case 'yes_no':
      return <YesNo {...props} />;
  }
}

function OutlineCheck({ checked }: { checked: boolean }) {
  return (
    <span
      className={cn(
        'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
        checked
          ? 'border-foreground text-foreground'
          : 'border-muted-foreground/50 [&_svg]:invisible',
      )}
    >
      <Check className="h-3 w-3" aria-hidden />
    </span>
  );
}

function OptionList({ field, value, options, onChange }: RuleValueEditorProps) {
  const selected = new Set(Array.isArray(value) ? (value as string[]) : []);
  const list = fieldValues(field, options);
  const toggle = (option: WhereOption) => {
    const next = selected.has(option.value)
      ? [...selected].filter((v) => v !== option.value)
      : [...selected, option.value];
    onChange(next);
  };
  return (
    <Command>
      <CommandInput placeholder={`${field.label}…`} autoFocus />
      <CommandList className="max-h-[min(300px,45vh)]">
        <CommandEmpty>
          {list.length === 0 ? 'Nothing to pick yet.' : 'No values found.'}
        </CommandEmpty>
        <CommandGroup>
          {list.map((option) => (
            <CommandItem
              key={option.value}
              value={`${option.label} ${option.value}`}
              onSelect={() => toggle(option)}
            >
              <OutlineCheck checked={selected.has(option.value)} />
              <span className="flex min-w-0 flex-col">
                <span className="truncate">{option.label}</span>
                {option.description ? (
                  <span className="truncate text-xs text-muted-foreground">
                    {option.description}
                  </span>
                ) : null}
              </span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

function TextInput({ field, value, onChange, onSubmit }: RuleValueEditorProps) {
  return (
    <div className="p-2">
      <Input
        autoFocus
        aria-label={field.label}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSubmit?.();
        }}
        className="h-8"
      />
    </div>
  );
}

/** Each Enter adds a value pill; "is Acme, Beta" means either. Typed text counts too, so Apply keeps it. */
function TextList({ field, value, onChange, onSubmit }: RuleValueEditorProps) {
  const [text, setText] = React.useState('');
  // Whether the typed text rides along as the last value until Enter makes it a pill.
  const [riding, setRiding] = React.useState(false);
  const all = Array.isArray(value) ? (value as string[]) : [];
  const pills = riding ? all.slice(0, -1) : all;
  const pending = text.trim();
  const type = (next: string) => {
    const typed = next.trim();
    const ride = typed.length > 0 && !pills.includes(typed);
    setText(next);
    setRiding(ride);
    onChange(ride ? [...pills, typed] : pills);
  };
  const remove = (v: string) => {
    const kept = pills.filter((x) => x !== v);
    onChange(riding ? [...kept, pending] : kept);
  };
  return (
    <div className="flex flex-col gap-2 p-2">
      <Input
        autoFocus
        aria-label={field.label}
        placeholder="Type a value and press Enter"
        value={text}
        onChange={(e) => type(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          if (!pending) {
            onSubmit?.();
            return;
          }
          setText('');
          setRiding(false);
        }}
        className="h-8"
      />
      {pills.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {pills.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs"
            >
              {v}
              <button type="button" aria-label={`Remove ${v}`} onClick={() => remove(v)}>
                <X className="h-3 w-3 text-muted-foreground" aria-hidden />
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const toNumber = (text: string): number | undefined => {
  const cleaned = text.replace(/[,\s$]/g, '');
  if (!cleaned) return undefined;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : undefined;
};

function NumberField({
  label,
  value,
  onChange,
  onSubmit,
  autoFocus,
}: {
  label: string;
  value: unknown;
  onChange: (n: number | undefined) => void;
  onSubmit?: () => void;
  autoFocus?: boolean;
}) {
  const [text, setText] = React.useState(typeof value === 'number' ? String(value) : '');
  return (
    <Input
      autoFocus={autoFocus}
      aria-label={label}
      inputMode="decimal"
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onChange(toNumber(e.target.value));
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onSubmit?.();
      }}
      className="h-8"
    />
  );
}

function NumberInput({ field, value, onChange, onSubmit }: RuleValueEditorProps) {
  return (
    <div className="p-2">
      <NumberField
        autoFocus
        label={field.label}
        value={value}
        onChange={onChange}
        onSubmit={onSubmit}
      />
    </div>
  );
}

function Pair({
  value,
  onChange,
  onSubmit,
  labels,
}: Pick<RuleValueEditorProps, 'value' | 'onChange' | 'onSubmit'> & { labels: [string, string] }) {
  const pair = Array.isArray(value) ? (value as Array<number | undefined>) : [undefined, undefined];
  return (
    <div className="flex items-center gap-2 p-2 text-xs text-muted-foreground">
      <NumberField
        autoFocus
        label={labels[0]}
        value={pair[0]}
        onChange={(n) => onChange([n, pair[1]])}
        onSubmit={onSubmit}
      />
      <span>and</span>
      <NumberField
        label={labels[1]}
        value={pair[1]}
        onChange={(n) => onChange([pair[0], n])}
        onSubmit={onSubmit}
      />
    </div>
  );
}

function NumberPair(props: RuleValueEditorProps) {
  return <Pair {...props} labels={['From', 'To']} />;
}

/** The workspace's tier names first (each a range), then a custom range. */
function ScoreRange(props: RuleValueEditorProps) {
  const tiers = fieldTiers(props.field, props.options);
  const current = Array.isArray(props.value) ? (props.value as number[]) : [];
  const [custom, setCustom] = React.useState(
    current.length === 2 && !tiers.some((t) => t.value === `${current[0]}-${current[1]}`),
  );
  return (
    <div className="flex flex-col p-1">
      {tiers.map((tier) => {
        const [lo, hi] = tier.value.split('-').map(Number);
        const checked = !custom && current[0] === lo && current[1] === hi;
        return (
          <button
            key={tier.value}
            type="button"
            onClick={() => {
              setCustom(false);
              props.onChange([lo, hi]);
            }}
            className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
          >
            <OutlineCheck checked={checked} />
            <span>{tier.label}</span>
            <span className="ml-auto text-xs text-muted-foreground">{tier.description}</span>
          </button>
        );
      })}
      <button
        type="button"
        onClick={() => setCustom(true)}
        className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
      >
        <OutlineCheck checked={custom} />
        Or a range
      </button>
      {custom ? <Pair {...props} labels={['From', 'To']} /> : null}
    </div>
  );
}

function AgoInput({ value, onChange, onSubmit }: RuleValueEditorProps) {
  const ago = (value && typeof value === 'object' ? value : {}) as {
    amount?: number;
    unit?: string;
  };
  const unit = ago.unit === 'days' ? 'days' : 'months';
  return (
    <div className="flex items-center gap-2 p-2 text-xs text-muted-foreground">
      <div className="w-20">
        <NumberField
          autoFocus
          label="Amount"
          value={ago.amount}
          onChange={(n) => onChange({ amount: n, unit })}
          onSubmit={onSubmit}
        />
      </div>
      <select
        aria-label="Unit"
        value={unit}
        onChange={(e) => onChange({ amount: ago.amount, unit: e.target.value })}
        className="h-8 rounded-md border bg-background px-2 text-sm text-foreground"
      >
        {DATE_UNITS.map((u) => (
          <option key={u} value={u}>
            {UNIT_LABEL[u]}
          </option>
        ))}
      </select>
      <span>ago</span>
    </div>
  );
}

function DatePair({ value, onChange }: RuleValueEditorProps) {
  const pair = Array.isArray(value) ? (value as string[]) : ['', ''];
  const id = React.useId();
  return (
    <div className="flex flex-col gap-2 p-2 text-xs text-muted-foreground">
      <label htmlFor={`${id}-from`} className="flex items-center justify-between gap-2">
        From
        <Input
          id={`${id}-from`}
          type="date"
          value={pair[0] ?? ''}
          onChange={(e) => onChange([e.target.value, pair[1] ?? ''])}
          className="h-8 w-[160px]"
        />
      </label>
      <label htmlFor={`${id}-to`} className="flex items-center justify-between gap-2">
        To
        <Input
          id={`${id}-to`}
          type="date"
          value={pair[1] ?? ''}
          onChange={(e) => onChange([pair[0] ?? '', e.target.value])}
          className="h-8 w-[160px]"
        />
      </label>
    </div>
  );
}

function YesNo({ value, onChange }: RuleValueEditorProps) {
  return (
    <div className="flex gap-2 p-2">
      {[true, false].map((answer) => (
        <Button
          key={String(answer)}
          type="button"
          size="sm"
          variant={value === answer ? 'secondary' : 'outline'}
          aria-pressed={value === answer}
          onClick={() => onChange(answer)}
          className="flex-1"
        >
          {answer ? 'Yes' : 'No'}
        </Button>
      ))}
    </div>
  );
}
